import { useState, useEffect } from "react";
import { normalizeLoginCredentials, requestLogin } from "../utils/authLogin";
import { useThemeStore } from "./themeStore";

let authData = {
  user: null,
  token: null,
};
let sessionVersion = 0;

const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) =>
    listener({ ...authData })
  );
};

export const authStore = {
  getAuthData: () => authData,
  getSessionVersion: () => sessionVersion,

  isAuthenticated: () => !!authData.token,

  login: async (companyCode, username, password) => {
    const version = sessionVersion;
    const credentials = normalizeLoginCredentials({
      company_code: companyCode,
      employee_code: username,
      password,
    });
    const data = await requestLogin(credentials);
    if (sessionVersion !== version) {
      throw new Error("Session changed during login. Please try again.");
    }

    document.cookie = `auth_creds=${encodeURIComponent(
      JSON.stringify(credentials)
    )}; max-age=2592000; path=/; SameSite=Lax`;
    authStore.setuserdata(data.data, data.access_token);
    return data;
  },

  setuserdata: (user, token) => {
  sessionVersion += 1;
  authData = {
    user,
    token,
  };
  useThemeStore.getState().setTheme(user?.theme);

  document.cookie = `auth_token=${encodeURIComponent(
    token
  )}; max-age=2592000; path=/; SameSite=Lax`;

  document.cookie = `auth_user=${encodeURIComponent(
    JSON.stringify(user)
  )}; max-age=2592000; path=/; SameSite=Lax`;

  notifyListeners();
},

  updateUserTheme: (theme, userId, companyId) => {
    if (!["light", "dark"].includes(theme) || !authData.token ||
        authData.user?.user_id !== userId || authData.user?.company_id !== companyId) return false;
    authData = { ...authData, user: { ...authData.user, theme } };
    document.cookie = `auth_user=${encodeURIComponent(JSON.stringify(authData.user))}; max-age=2592000; path=/; SameSite=Lax`;
    useThemeStore.getState().setTheme(theme);
    notifyListeners();
    return true;
  },

restoreSession: () => {
  const cookies = document.cookie.split("; ");

  const tokenCookie = cookies.find((c) =>
    c.startsWith("auth_token=")
  );

  const userCookie = cookies.find((c) =>
    c.startsWith("auth_user=")
  );

  if (!tokenCookie || !userCookie) {
    return false;
  }

  try {
    const token = decodeURIComponent(
      tokenCookie.split("=")[1]
    );

    const user = JSON.parse(
      decodeURIComponent(userCookie.split("=")[1])
    );

    sessionVersion += 1;
    authData = {
      user,
      token,
    };
    useThemeStore.getState().setTheme(user?.theme);

    notifyListeners();

    return true;
  } catch (e) {
    return false;
  }
},

  logout: () => {
  sessionVersion += 1;
  document.cookie =
    "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

  document.cookie =
    "auth_user=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

  document.cookie =
    "auth_creds=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

  authData = {
    user: null,
    token: null,
  };

  notifyListeners();
},
};

export const useAuthStore = () => {
  const [state, setState] = useState(authData);

  useEffect(() => {
    listeners.add(setState);

    return () => listeners.delete(setState);
  }, []);

  return {
    user: state.user,
    token: state.token,
    isAuthenticated: authStore.isAuthenticated(),
    login: authStore.login,
    setuserdata: authStore.setuserdata,
    logout: authStore.logout,
    restoreSession: authStore.restoreSession,
  };
};
